import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AIAssistant from './AIAssistant';
import CreateAdmin from './CreateAdmin';
import LiveMap from './LiveMap';
import Settings from './Settings';

const API = 'https://weridesudan.com/api/admin';

// ─── Shared overlay ───────────────────────────────────────────────────────────

function Overlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <Overlay onClose={onCancel}>
      <h3 style={styles.modalTitle}>Confirm</h3>
      <p style={{ color: '#444', margin: '0 0 20px' }}>{message}</p>
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onCancel}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: '#e53935' }} onClick={onConfirm}>Delete</button>
      </div>
    </Overlay>
  );
}

// ─── Add user modal ────────────────────────────────────────────────────────────

function AddUserModal({ role, token, onClose, onDone }: {
  role: 'driver' | 'rider';
  token: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: 'WeRide123!' });
  const [err, setErr] = useState('');
  const h = { Authorization: `Bearer ${token}` };
  const label = role === 'driver' ? 'Driver' : 'Rider';

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { setErr('Name and phone are required.'); return; }
    try {
      await axios.post(`${API}/users`, { ...form, role }, { headers: h });
      onDone();
      onClose();
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Failed to create user.');
    }
  };

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>Add New {label}</h3>
      {(['name', 'phone', 'email'] as const).map(f => (
        <input
          key={f}
          style={styles.formInput}
          placeholder={f.charAt(0).toUpperCase() + f.slice(1)}
          value={form[f]}
          onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))}
        />
      ))}
      {err && <p style={{ color: '#e53935', fontSize: 13, margin: '4px 0 8px' }}>{err}</p>}
      <div style={styles.modalActions}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#9ca3af' }} onClick={onClose}>Cancel</button>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={submit}>Create {label}</button>
      </div>
    </Overlay>
  );
}

// ─── Profile modal ─────────────────────────────────────────────────────────────

function ProfileModal({ user, onClose }: { user: any; onClose: () => void }) {
  const rows: [string, React.ReactNode][] = [
    ['Role', user.role?.toUpperCase() || '–'],
    ['Phone', user.phone || '–'],
    ['Email', user.email || '–'],
    ['Account', user.isActive
      ? <span style={{ ...styles.badge, backgroundColor: '#4caf50' }}>Active</span>
      : <span style={{ ...styles.badge, backgroundColor: '#f44336' }}>Suspended</span>],
    ['Online', user.isOnline ? '🟢 Online' : '🔴 Offline'],
    ['Avg Rating', user.rating ? `⭐ ${Number(user.rating).toFixed(2)}` : '–'],
    ['Total Rides', user.totalRides ?? '–'],
    ['Member Since', user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '–'],
  ];

  return (
    <Overlay onClose={onClose}>
      <h3 style={styles.modalTitle}>{user.name}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '9px 4px', color: '#888', width: 110 }}>{k}</td>
              <td style={{ padding: '9px 4px', fontWeight: 500, color: '#111' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ textAlign: 'right', marginTop: 18 }}>
        <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────

function Login({ onLogin }: { onLogin: (token: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async () => {
    try {
      const res = await axios.post(`${API}/login`, { email, password });
      onLogin(res.data.token);
    } catch {
      setError('Invalid credentials');
    }
  };

  return (
    <div style={styles.loginContainer}>
      <div style={styles.loginBox}>
        <h1 style={styles.logo}>🚗 We Ride Sudan</h1>
        <h2 style={styles.loginTitle}>Admin Dashboard</h2>
        <input style={styles.input} placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
        <input style={styles.input} placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} />
        {error && <p style={styles.error}>{error}</p>}
        <button style={styles.button} onClick={handleLogin}>Login</button>
      </div>
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────

function Dashboard({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [tab, setTab] = useState('rides');
  const [rides, setRides] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);

  // Modal / dialog state
  const [addModalRole, setAddModalRole] = useState<'driver' | 'rider' | null>(null);
  const [profileUser, setProfileUser] = useState<any>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [selectedRideIds, setSelectedRideIds] = useState<Set<string>>(new Set());

  const h = { Authorization: `Bearer ${token}` };

  const reloadUsers = async () => {
    const res = await axios.get(`${API}/users`, { headers: h });
    setUsers(res.data.users || []);
  };
  const reloadRides = async () => {
    const res = await axios.get(`${API}/rides`, { headers: h });
    setRides(res.data.rides || []);
  };

  useEffect(() => {
    reloadRides();
    reloadUsers();
    axios.get(`${API}/stats`, { headers: h }).then(r => setStats(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const drivers = users.filter(u => u.role === 'driver');
  const riders  = users.filter(u => u.role === 'rider');

  const starRating = (val: number | null | undefined) =>
    val != null ? `⭐ ${Number(val).toFixed(1)}` : '—';

  // ── User actions ──────────────────────────────────────────────────────────

  const toggleUser = async (id: string, isActive: boolean) => {
    await axios.patch(`${API}/users/${id}`, { isActive: !isActive }, { headers: h });
    await reloadUsers();
  };

  const deleteUser = (id: string, name: string) => {
    setPendingConfirm({
      message: `Delete "${name}" permanently? This cannot be undone.`,
      onConfirm: async () => {
        await axios.delete(`${API}/users/${id}`, { headers: h });
        await reloadUsers();
        setPendingConfirm(null);
      },
    });
  };

  // ── Ride selection ────────────────────────────────────────────────────────

  const toggleRide = (id: string) => {
    setSelectedRideIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleAllRides = () => {
    setSelectedRideIds(prev =>
      prev.size === rides.length ? new Set() : new Set(rides.map(r => r._id))
    );
  };

  const deleteSelectedRides = () => {
    const count = selectedRideIds.size;
    setPendingConfirm({
      message: `Delete ${count} selected ride${count !== 1 ? 's' : ''} permanently?`,
      onConfirm: async () => {
        await Promise.all(Array.from(selectedRideIds).map(id => axios.delete(`${API}/rides/${id}`, { headers: h })));
        await reloadRides();
        setSelectedRideIds(new Set());
        setPendingConfirm(null);
      },
    });
  };

  // ── Shared row styles ─────────────────────────────────────────────────────

  const clickableRow: React.CSSProperties = { cursor: 'pointer' };
  const hoverRow = (e: React.MouseEvent<HTMLTableRowElement>) =>
    (e.currentTarget.style.backgroundColor = '#f9fafb');
  const unhoverRow = (e: React.MouseEvent<HTMLTableRowElement>) =>
    (e.currentTarget.style.backgroundColor = '');

  const tabs = ['rides', 'drivers', 'riders', 'stats', 'ai', 'live-map', 'settings', 'create-admin'];
  const tabLabel: Record<string, string> = {
    rides: '🚗 Rides', drivers: '🧑 Drivers', riders: '👤 Riders',
    stats: '📊 Statistics', ai: '🤖 AI Assistant', 'live-map': '🗺 Live Map',
    settings: '⚙️ Settings', 'create-admin': '➕ Create Admin',
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <h2 style={styles.sidebarTitle}>🚗 WeRide Admin</h2>
        {tabs.map(t => (
          <div key={t} style={{ ...styles.navItem, ...(tab === t ? styles.navActive : {}) }} onClick={() => setTab(t)}>
            {tabLabel[t]}
          </div>
        ))}
        <div style={{ marginTop: 'auto' }}>
          <div style={styles.navItem} onClick={onLogout}>🚪 Logout</div>
        </div>
      </div>

      {/* Main content */}
      <div style={styles.main}>

        {/* ── STATS ── */}
        {tab === 'stats' && stats && (
          <div>
            <h2>📊 Overview</h2>
            <div style={styles.statsGrid}>
              {[
                ['Total Rides', stats.totalRides],
                ['Active Rides', stats.activeRides],
                ['Total Drivers', stats.totalDrivers],
                ['Total Riders', stats.totalRiders],
                ['Total Revenue', `${stats.totalRevenue} SDG`],
              ].map(([label, value]) => (
                <div key={label} style={styles.statCard}>
                  <div style={{ fontSize: 32, fontWeight: 'bold' }}>{value}</div>
                  <div>{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── RIDES ── */}
        {tab === 'rides' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>🚗 All Rides</h2>
              {selectedRideIds.size > 0 && (
                <button style={{ ...styles.actionBtn, backgroundColor: '#e53935' }} onClick={deleteSelectedRides}>
                  Delete Selected ({selectedRideIds.size})
                </button>
              )}
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: 36, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={rides.length > 0 && selectedRideIds.size === rides.length}
                      onChange={toggleAllRides}
                    />
                  </th>
                  <th>Rider</th><th>Driver</th><th>Pickup</th><th>Dropoff</th>
                  <th>Fare</th><th>Status</th><th>Ratings</th>
                </tr>
              </thead>
              <tbody>
                {rides.map(r => (
                  <tr key={r._id}>
                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRideIds.has(r._id)}
                        onChange={() => toggleRide(r._id)}
                      />
                    </td>
                    <td>{r.rider?.name || 'N/A'}</td>
                    <td>{r.driver?.name || 'Unassigned'}</td>
                    <td>{r.pickup?.address || '–'}</td>
                    <td>{r.destination?.address || '–'}</td>
                    <td>{r.fare?.estimated} SDG</td>
                    <td>
                      <span style={{ ...styles.badge, backgroundColor: r.status === 'completed' ? '#4caf50' : r.status === 'cancelled' ? '#f44336' : '#ff9800' }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, lineHeight: '1.6' }}>
                      {r.status === 'completed' ? (
                        <><div>🚗 {starRating(r.driverRating ?? r.rating)}</div><div>👤 {starRating(r.riderRating)}</div></>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── DRIVERS ── */}
        {tab === 'drivers' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>🧑 Drivers</h2>
              <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={() => setAddModalRole('driver')}>
                + Add New Driver
              </button>
            </div>
            <table style={styles.table}>
              <thead>
                <tr><th>Name</th><th>Phone</th><th>Online</th><th>Account</th><th>Rating</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {drivers.map(d => (
                  <tr
                    key={d._id}
                    style={clickableRow}
                    onMouseEnter={hoverRow}
                    onMouseLeave={unhoverRow}
                    onClick={() => setProfileUser(d)}
                  >
                    <td>{d.name}</td>
                    <td>{d.phone}</td>
                    <td>{d.isOnline ? '🟢 Online' : '🔴 Offline'}</td>
                    <td>
                      <span style={{ ...styles.badge, backgroundColor: d.isActive ? '#4caf50' : '#f44336' }}>
                        {d.isActive ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td>{starRating(d.rating)}</td>
                    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, padding: '8px 12px' }}>
                      <button
                        style={{ ...styles.actionBtn, backgroundColor: d.isActive ? '#f59e0b' : '#43a047', fontSize: 12 }}
                        onClick={() => toggleUser(d._id, d.isActive)}
                      >
                        {d.isActive ? 'Suspend' : 'Activate'}
                      </button>
                      <button
                        style={{ ...styles.actionBtn, backgroundColor: '#e53935', fontSize: 12 }}
                        onClick={() => deleteUser(d._id, d.name)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── RIDERS ── */}
        {tab === 'riders' && (
          <div>
            <div style={styles.tabHeader}>
              <h2 style={{ margin: 0 }}>👤 Riders</h2>
              <button style={{ ...styles.actionBtn, backgroundColor: '#1a73e8' }} onClick={() => setAddModalRole('rider')}>
                + Add New Rider
              </button>
            </div>
            <table style={styles.table}>
              <thead>
                <tr><th>Name</th><th>Phone</th><th>Email</th><th>Account</th><th>Total Rides</th><th>Rating</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {riders.map(r => (
                  <tr
                    key={r._id}
                    style={clickableRow}
                    onMouseEnter={hoverRow}
                    onMouseLeave={unhoverRow}
                    onClick={() => setProfileUser(r)}
                  >
                    <td>{r.name}</td>
                    <td>{r.phone}</td>
                    <td>{r.email}</td>
                    <td>
                      <span style={{ ...styles.badge, backgroundColor: r.isActive ? '#4caf50' : '#f44336' }}>
                        {r.isActive ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    <td>{r.totalRides || 0}</td>
                    <td>{starRating(r.rating)}</td>
                    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, padding: '8px 12px' }}>
                      <button
                        style={{ ...styles.actionBtn, backgroundColor: r.isActive ? '#f59e0b' : '#43a047', fontSize: 12 }}
                        onClick={() => toggleUser(r._id, r.isActive)}
                      >
                        {r.isActive ? 'Suspend' : 'Activate'}
                      </button>
                      <button
                        style={{ ...styles.actionBtn, backgroundColor: '#e53935', fontSize: 12 }}
                        onClick={() => deleteUser(r._id, r.name)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── OTHER TABS ── */}
        {tab === 'ai'           && <AIAssistant token={token} />}
        {tab === 'live-map'     && <LiveMap />}
        {tab === 'settings'     && <Settings token={token} />}
        {tab === 'create-admin' && <CreateAdmin token={token} />}
      </div>

      {/* ── Modals ── */}
      {addModalRole && (
        <AddUserModal
          role={addModalRole}
          token={token}
          onClose={() => setAddModalRole(null)}
          onDone={reloadUsers}
        />
      )}
      {profileUser && (
        <ProfileModal user={profileUser} onClose={() => setProfileUser(null)} />
      )}
      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}

// ─── App root ──────────────────────────────────────────────────────────────────

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');

  const handleLogin = (t: string) => { localStorage.setItem('adminToken', t); setToken(t); };
  const handleLogout = () => { localStorage.removeItem('adminToken'); setToken(''); };

  return token ? <Dashboard token={token} onLogout={handleLogout} /> : <Login onLogin={handleLogin} />;
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles: any = {
  // Login
  loginContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#1a1a2e' },
  loginBox: { backgroundColor: '#fff', padding: 40, borderRadius: 12, width: 380, textAlign: 'center' as const },
  logo: { fontSize: 24, marginBottom: 8 },
  loginTitle: { color: '#333', marginBottom: 24 },
  input: { width: '100%', padding: 12, marginBottom: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 15, boxSizing: 'border-box' as const },
  button: { width: '100%', padding: 14, backgroundColor: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, cursor: 'pointer' },
  error: { color: 'red', marginBottom: 12 },
  // Layout
  container: { display: 'flex', height: '100vh', fontFamily: 'sans-serif' },
  sidebar: { width: 220, backgroundColor: '#1a1a2e', color: '#fff', padding: 20, display: 'flex', flexDirection: 'column' as const },
  sidebarTitle: { fontSize: 16, marginBottom: 24 },
  navItem: { padding: '12px 16px', cursor: 'pointer', borderRadius: 8, marginBottom: 8, textTransform: 'capitalize' as const },
  navActive: { backgroundColor: '#1a73e8' },
  main: { flex: 1, padding: 30, backgroundColor: '#f5f5f5', overflowY: 'auto' as const },
  // Stats
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 20 },
  statCard: { backgroundColor: '#fff', padding: 24, borderRadius: 12, textAlign: 'center' as const, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  // Tables
  table: { width: '100%', borderCollapse: 'collapse' as const, backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
  badge: { padding: '3px 10px', borderRadius: 20, color: '#fff', fontSize: 12, display: 'inline-block' },
  actionBtn: { padding: '6px 14px', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontSize: 13 },
  tabHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  // Modals
  overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: '#fff', borderRadius: 14, padding: 28, width: 440, maxWidth: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' },
  modalTitle: { marginTop: 0, marginBottom: 18, color: '#111', fontSize: 18 },
  modalActions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 },
  formInput: { display: 'block', width: '100%', padding: '10px 12px', marginBottom: 10, borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' as const },
};
